import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Popover,
  Stack,
  TextField,
  Typography,
  Collapse,
  Divider,
  List,
  ListItem,
  ListItemText,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import NoteAddOutlinedIcon from '@mui/icons-material/NoteAddOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AssignmentIndOutlinedIcon from '@mui/icons-material/AssignmentIndOutlined';
import CloseIcon from '@mui/icons-material/Close';
import { useRecoilValue } from 'recoil';
import DynamicFormFieldsList from '../../../../components/Leads/DynamicFormFields/List.tsx';
import { SearchableSelect } from '../../../../components/ui';
import { userInformationAtom } from '../../../../Services/_states';
import useInterveneSx from '../../hooks/useInterveneSx';
import useLeadAssignment from '../../../business-functions/records/hooks/useLeadAssignment';
import { isUserAssigned } from '../../../business-functions/records/utils/leadAssignmentUtils';
import  {radiusPx } from '../../../../theme/tokens/radius';
import { states } from '../../../../theme/tokens/semantic.js';

export default function LeadDetailsFormPanel({
  phone,
  dynamicFormData,
  fieldErrors,
  phoneError,
  formEnable,
  formDataLoading,
  isLoading,
  onFieldChange,
  onSave,
  onEdit,
  onBack,
  onCancel,
  hasFormFields,
  isCreatingLead,
  canEditLead = false,
  canAssignLead = false,
  campaignId,
  selectedLead = null,
  selectedLeadId,
  onPhoneChange,
}) {
  const styles = useInterveneSx().contextDrawer;
  const loggedInUser = useRecoilValue(userInformationAtom);
  const profileId = loggedInUser?.profile?.id ?? loggedInUser?.id;
  const isReadOnly = formEnable && !isCreatingLead;
  const mode = isCreatingLead ? 'create' : isReadOnly ? 'view' : 'edit';

  const [showNotepad, setShowNotepad] = useState(false);
  const [noteText, setNoteText] = useState('');
  const [notes, setNotes] = useState([]);
  const [assignAnchorEl, setAssignAnchorEl] = useState(null);
  const [assignmentOverride, setAssignmentOverride] = useState(null);
  const leadIdForAssignment = selectedLead?.lead_id || selectedLead?.id || '';
  const assignmentRow = useMemo(
    () => (assignmentOverride === null
      ? selectedLead
      : { ...selectedLead, lead_assignment: assignmentOverride }),
    [assignmentOverride, selectedLead]
  );
  const {
    users: assignableUsers,
    loadingUsers,
    assignedUsers,
    selectedUsers,
    setSelectedUsers,
    queueUnassign,
    hasChanges,
    isSaving: isAssigning,
    submit: submitAssignment,
  } = useLeadAssignment({
    open: Boolean(assignAnchorEl),
    campaignId,
    leadIds: leadIdForAssignment ? [leadIdForAssignment] : [],
    selectedRow: assignmentRow,
    showCurrentAssignees: true,
  });

  useEffect(() => {
    setAssignmentOverride(null);
  }, [leadIdForAssignment]);

  const handleAssignmentSave = async () => {
    const result = await submitAssignment();
    if (!result?.ok) return;
    setAssignmentOverride([...assignedUsers, ...selectedUsers]);
    setAssignAnchorEl(null);
  };

  const handleAddNote = () => {
    if (!noteText.trim()) return;
    setNotes((prev) => [...prev, { id: Date.now(), text: noteText.trim() }]);
    setNoteText('');
  };

  const handleDeleteNote = (id) => {
    setNotes((prev) => prev.filter((n) => n.id !== id));
  };

  const modeContent = {
    create: {
      title: 'Create lead',
      description: 'Complete the details below. A lead ID will be generated automatically.',
      label: 'New',
      color: 'success',
    },
    view: {
      title: 'Lead details',
      description: selectedLeadId ? `Lead ${selectedLeadId}` : 'Review the saved lead information.',
      label: 'Read only',
      color: 'default',
    },
    edit: {
      title: 'Edit lead',
      description: selectedLeadId ? `Updating lead ${selectedLeadId}` : 'Update the lead information.',
      label: 'Editing',
      color: 'warning',
    },
  }[mode];

  if (formDataLoading) {
    return (
      <Box sx={styles.leadFormSection}>
        <Box sx={styles.leadFormLoading}>
          <CircularProgress size={24} />
          <Typography variant="body2" color="text.secondary">
            Loading lead form…
          </Typography>
        </Box>
      </Box>
    );
  }

  if (!hasFormFields) {
    return (
      <Box sx={styles.leadFormSection}>
        <Box sx={styles.leadFormHeader}>
          <IconButton
            size="small"
            onClick={onBack}
            aria-label="Back to leads"
            sx={styles.leadFormBackButton}
          >
            <ArrowBackIcon fontSize="small" />
          </IconButton>
          <Typography sx={styles.leadFormTitle}>Lead details</Typography>
        </Box>
        <Box sx={styles.leadFormEmpty}>
          <Typography variant="subtitle2">No fields available</Typography>
          <Typography variant="body2" color="text.secondary">
            This lead form does not contain any agent-editable fields.
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        ...styles.leadFormSection,
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        minHeight: 0,
        height: '100%',
        overflow: 'hidden',
        position: 'relative',
      }}
    >
      {/* ===== FIXED HEADER ===== */}
      <Box
        sx={{
          ...styles.leadFormHeader,
          flexShrink: 0,
          borderBottom: '1px solid',
          borderColor: 'divider',
          pb: 1.5,
        }}
      >
        <IconButton
          size="small"
          onClick={onBack}
          aria-label="Back to leads"
          sx={styles.leadFormBackButton}
        >
          <ArrowBackIcon fontSize="small" />
        </IconButton>

        <Box sx={styles.leadFormHeading}>
          <Box sx={styles.leadFormTitleRow}>
            <Typography sx={styles.leadFormTitle}>{modeContent.title}</Typography>
            <Chip
              label={modeContent.label}
              color={modeContent.color}
              size="small"
              variant={mode === 'view' ? 'outlined' : 'filled'}
              sx={styles.leadFormModeChip}
            />
          </Box>
          <Typography sx={styles.leadFormDescription}>
            {modeContent.description}
          </Typography>
        </Box>

        <Box sx={{ ...styles.leadFormActions, display: 'flex', alignItems: 'center', gap: 1 }}>
          {canAssignLead && leadIdForAssignment ? (
            <Button
              variant="outlined"
              size="small"
              startIcon={<AssignmentIndOutlinedIcon />}
              onClick={(event) => setAssignAnchorEl(event.currentTarget)}
            >
              Assign lead
            </Button>
          ) : null}
          {isReadOnly ? (
            <Button
              variant="outlined"
              size="small"
              startIcon={canEditLead ? <EditOutlinedIcon /> : <VisibilityOutlinedIcon />}
              onClick={canEditLead ? onEdit : undefined}
              disabled={!canEditLead}
            >
              {canEditLead ? 'Edit lead' : 'View lead'}
            </Button>
          ) : null}
          <Popover
            open={Boolean(assignAnchorEl)}
            anchorEl={assignAnchorEl}
            onClose={() => {
              if (!isAssigning) setAssignAnchorEl(null);
            }}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          >
            <Box sx={{ width: { xs: 300, sm: 360 }, maxWidth: 'calc(100vw - 32px)', p: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1.5 }}>
                Assign lead
              </Typography>
              {assignedUsers.length > 0 ? (
                <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap" sx={{ mb: 1.5 }}>
                  {assignedUsers.map((user) => (
                    <Chip
                      key={user.id}
                      size="small"
                      color="success"
                      label={user.label || user.username}
                      onDelete={() => queueUnassign(user)}
                      deleteIcon={<CloseIcon />}
                      disabled={isAssigning}
                    />
                  ))}
                </Stack>
              ) : null}
              <SearchableSelect
                multiple
                options={assignableUsers}
                value={selectedUsers}
                onChange={(value) => setSelectedUsers(Array.isArray(value) ? value : [])}
                label="Select users"
                labelKey="username"
                valueKey="id"
                getOptionLabel={(user) => user.label || user.username || ''}
                loading={loadingUsers}
                disabled={isAssigning}
                getOptionDisabled={(option) => isUserAssigned(assignedUsers, option)}
                noOptionsText="No users available for this campaign"
              />
              <Stack direction="row" justifyContent="flex-end" spacing={1} sx={{ mt: 1.5 }}>
                <Button
                  size="small"
                  onClick={() => setAssignAnchorEl(null)}
                  disabled={isAssigning}
                >
                  Cancel
                </Button>
                <Button
                  size="small"
                  variant="contained"
                  onClick={handleAssignmentSave}
                  disabled={isAssigning || !hasChanges}
                >
                  {isAssigning ? 'Saving…' : 'Save'}
                </Button>
              </Stack>
            </Box>
          </Popover>
        </Box>
      </Box>

      {/* ===== SCROLLABLE BODY ===== */}
      <Box
        sx={{
          flex: '1 1 auto',
          minHeight: 0,       // ✅ IMPORTANT
          overflowY: 'auto',
          overflowX: 'hidden',
          px: 2,
          py: 2,
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          '&::-webkit-scrollbar': { width: 6 },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: 'rgba(0,0,0,0.2)',
            borderRadius: 3,
          },
        }}
      >
        {/* Phone Number */}
        <TextField
          label="Phone Number"
          value={phone}
          onChange={(event) => onPhoneChange?.(event.target.value)}
          disabled={!isCreatingLead}
          inputProps={{ inputMode: 'tel' }}
          required
          error={Boolean(phoneError)}
          helperText={phoneError}
          fullWidth
          variant="outlined"
          size="small"
          sx={{'& .MuiInputBase-root':{ overflow: 'hidden' }}}
        />

        {/* Dynamic Form Fields */}
        <Box sx={{ '& .MuiRadioGroup-root > .MuiFormControlLabel-root': { mr: 0 , pl:0 } , '& .MuiButtonBase-root.MuiRadio-root':{pl:0} }}>
          <DynamicFormFieldsList
            data={dynamicFormData || {}}
            userId={profileId}
            onChange={onFieldChange}
            fieldErrors={fieldErrors}
            disabled={formEnable}
            phoneNumber={phone}
            gridSize={12}
            variant="compact"
            isCreatingLead={isCreatingLead}
          />
        </Box>

        {/* ===== NOTEPAD SECTION ===== */}
        <Box
          sx={{
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1.5,
            overflow: 'hidden',
            backgroundColor: 'background.paper',
            flexShrink: 0,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              px: 1.5,
              py: 1,
              backgroundColor: 'action.hover',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <NoteAddOutlinedIcon fontSize="small" color="action" />
              <Typography variant="subtitle2" fontWeight={600}>
                Private Notepad
              </Typography>
              {notes.length > 0 && (
                <Chip
                  label={notes.length}
                  size="small"
                  color="primary"
                  sx={{ height: 20, fontSize: 11 }}
                />
              )}
            </Box>

            <Button
              variant={showNotepad ? 'contained' : 'outlined'}
              size="small"
              startIcon={<NoteAddOutlinedIcon fontSize="small" />}
              endIcon={showNotepad ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              onClick={() => setShowNotepad((prev) => !prev)}
              sx={{ textTransform: 'none', borderRadius: radiusPx.md , minWidth: 110 }}
            >
              {showNotepad ? 'Hide' : 'Add Note'}
            </Button>
          </Box>

          <Collapse in={showNotepad} timeout="auto" unmountOnExit>
            <Divider />
            <Box
              sx={{
                p: 1.5,
                backgroundColor: 'background.default',
                display: 'flex',
                flexDirection: 'column',
                gap: 1.5,
              }}
            >
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                <TextField
                  fullWidth
                  size="small"
                  multiline
                  minRows={2}
                  maxRows={4}
                  placeholder="Write a private note…"
                  value={noteText}
                  onChange={(e) => setNoteText(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
                      handleAddNote();
                    }
                  }}
                />
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={handleAddNote}
                  disabled={!noteText.trim()}
                  sx={{
                    textTransform: 'none',
                    height: 40,
                    flexShrink: 0,
                    borderRadius: 1.5,
                  }}
                >
                  Add
                </Button>
              </Box>

              {notes.length === 0 ? (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ textAlign: 'center', py: 1 }}
                >
                  No notes added yet. Start typing above.
                </Typography>
              ) : (
                <List dense sx={{ p: 0, m: 0 }}>
                  {notes.map((n) => (
                    <ListItem
                      key={n.id}
                      sx={{
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 1,
                        mb: 0.5,
                        bgcolor: 'background.paper',
                        alignItems: 'flex-start',
                        '& .MuiListItemSecondaryAction-root': { top: '25px'},
                      }}
                      secondaryAction={
                        <IconButton
                          edge="end"
                          size="small"
                          onClick={() => handleDeleteNote(n.id)}
                          aria-label="Delete note"
                        >
                          <DeleteOutlineIcon fontSize="small" sx={{color:states.danger}} />
                        </IconButton>
                      }
                    >
                      <ListItemText
                        primary={n.text}
                        primaryTypographyProps={{
                          variant: 'body2',
                          sx: { whiteSpace: 'pre-wrap', wordBreak: 'break-word' },
                        }}
                      />
                    </ListItem>
                  ))}
                </List>
              )}
            </Box>
          </Collapse>
        </Box>
      </Box>

      {/* ===== FIXED FOOTER ===== */}
      {!isReadOnly && (
        <Box
          sx={{
            ...styles.leadFormFooter,
            flexShrink: 0,
            borderTop: '1px solid',
            borderColor: 'divider',
            px: 2,
            py: 1.5,
            backgroundColor: 'background.paper',
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 1,
          }}
        >
          <Button
            variant="text"
            size="small"
            onClick={onCancel}
            disabled={isLoading}
            sx={{ textTransform: 'none' , borderRadius: radiusPx.md , border: '1px solid', }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            size="small"
            startIcon={
              isLoading ? (
                <CircularProgress size={14} color="inherit" />
              ) : (
                <SaveOutlinedIcon />
              )
            }
            onClick={onSave}
            disabled={isLoading}
            sx={{ textTransform: 'none', borderRadius: radiusPx.md }}
          >
            {isLoading
              ? 'Saving…'
              : isCreatingLead
                ? 'Create lead'
                : 'Save changes'}
          </Button>
        </Box>
      )}
    </Box>
  );
}